import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { commonStyles, COLORS, formatExamDate, historyToString, calculateVolume, formatListItems, buildCombinedReason, getFacilityDisplayName } from '../utils/pdfHelpers';
import { PdfPageHeader, PdfPatientBox, PdfCallReport, PdfAccentSectionHeader, PdfSectionHeader, PdfLabelValue, PdfCommentsBox, PdfFooter } from '../utils/PdfSharedComponents';
import { PdfExamDetails, PdfMaternalSections, PdfMaternalMeasurements } from '../utils/PdfMaternalSections';
import { getConcordance } from '../utils/obDating';

function formatEGA(ega) {
  if (!ega) return '';
  if (typeof ega === 'string') return ega;
  const w = ega.weeks || ega.weeks === 0 ? ega.weeks : '';
  const d = ega.days  || ega.days  === 0 ? ega.days  : '';
  if (w === '' && d === '') return '';
  return [w !== '' ? `${w}w` : null, d !== '' ? `${d}d` : null].filter(Boolean).join(' ');
}

// Any non-numeric FHR (the form auto-fills "None Detected") means no heart rate to report
function isFHRNotDetected(fhr) {
  return !!fhr && isNaN(parseFloat(fhr));
}

function formatFHR(fhr) {
  return isFHRNotDetected(fhr) ? fhr : `${fhr} bpm`;
}

export function FirstTrimesterOBPDF({ data, facilityInfo }) {
  const reason = buildCombinedReason(data.reason, data.history);

  const gest = data.gestations || {};
  const ga = data.gestationalAge || {};
  const gestationsList = gest.gestationsList || [];

  // Nested <Text> segments so a not-detected FHR line can be red within the box
  const measurementLines = gestationsList.flatMap((g, i) => {
    const lines = [{ text: `Gestation ${i + 1}:` }];
    if (g.crl) lines.push({ text: `  CRL: ${g.crl} mm` });
    if (g.fhr) lines.push({ text: `  FHR: ${formatFHR(g.fhr)}`, isAbnormal: isFHRNotDetected(g.fhr) });
    return lines;
  });

  const clinicalEGA = formatEGA(ga.clinicalEGA);
  // Clinical dates are shared; ultrasound dates are per gestation, so label them when there are several
  const usLabelPrefix = (i) => (gestationsList.length > 1 ? `Gestation ${i + 1} ` : '');

  const maternal = {
    uterus: gest.uterus,
    priorCSection: gest.priorCSection,
    placentaScarDistance: gest.placentaScarDistance,
    uterusMasses: gest.uterusMasses,
    endometrium: data.endometrium,
    cervixLength: data.cervixLength,
    cervixNotVisualized: data.cervixNotVisualized,
    cervixFunneling: data.cervixFunneling,
    rightOvary: data.rightOvary,
    leftOvary: data.leftOvary,
    culDeSac: data.culDeSac
  };

  return (
    <Document>
      <Page size="LETTER" style={commonStyles.page}>
        <PdfPageHeader facilityInfo={facilityInfo} examTitle="FIRST TRIMESTER OB ULTRASOUND WORKSHEET" />
        <PdfPatientBox
          firstName={data.firstName}
          lastName={data.lastName}
          mrn={data.mrn}
          examDate={data.examDate}
          technologist={data.technologist}
          studentWorksheet={data.studentWorksheet}
          supervisingSonographer={data.supervisingSonographer}
          reason={reason}
        />
        <PdfCallReport show={data.callReport} phoneNumber={data.phoneNumber} />

        <View style={commonStyles.twoCol}>
          <View style={commonStyles.colLeft}>
            <PdfAccentSectionHeader title="MEASUREMENTS" />
            <View style={commonStyles.measurementBox}>
              <Text style={commonStyles.infoBoxText}>
                {measurementLines.length > 0 ? measurementLines.map((line, i) => (
                  <Text key={i} style={line.isAbnormal ? { color: COLORS.abnormal, fontFamily: 'Helvetica-Bold' } : {}}>
                    {i > 0 ? '\n' : ''}{line.text}
                  </Text>
                )) : 'None recorded'}
              </Text>
            </View>

            {/* Gestational Age — clinical and ultrasound dates side by side for comparison */}
            <View style={commonStyles.measurementBox}>
              <Text style={[commonStyles.infoBoxText, { fontFamily: 'Helvetica-Bold', marginBottom: 4 }]}>Gestational Age</Text>
              {ga.lmp ? <Text style={commonStyles.infoBoxText}>LMP: {ga.lmp}</Text> : null}
              {clinicalEGA ? <Text style={commonStyles.infoBoxText}>Clinical EGA: {clinicalEGA}</Text> : null}
              {ga.clinicalEDD ? <Text style={commonStyles.infoBoxText}>Clinical EDD: {ga.clinicalEDD}</Text> : null}
              {!ga.lmp && !clinicalEGA && !ga.clinicalEDD ? (
                <Text style={commonStyles.infoBoxText}>Clinical dates not provided</Text>
              ) : null}
              {gestationsList.map((g, i) => (
                <React.Fragment key={i}>
                  {formatEGA(g.ultrasoundEGA) ? <Text style={commonStyles.infoBoxText}>{usLabelPrefix(i)}US EGA: {formatEGA(g.ultrasoundEGA)}</Text> : null}
                  {g.ultrasoundEDD ? <Text style={commonStyles.infoBoxText}>{usLabelPrefix(i)}US EDD: {g.ultrasoundEDD}</Text> : null}
                </React.Fragment>
              ))}
            </View>

            <PdfMaternalMeasurements maternal={maternal} />

            <PdfCommentsBox comments={data.comments} />
          </View>

          <View style={commonStyles.colRight}>
            <PdfExamDetails examTypeDetails={data.examTypeDetails} />

            <PdfAccentSectionHeader title="GESTATIONS" />
            <PdfLabelValue label="Number and type:" value={gest.numberAndType || gest.type || ''} />
            {gest.numberAndType === 'Living multiple intrauterine gestations' && gest.type ? (
              <PdfLabelValue label="Chorionicity:" value={gest.type} />
            ) : null}
            {gestationsList.map((g, i) => {
              const concordance = getConcordance(g.ultrasoundEGA, ga.clinicalEGA);
              return (
                <View key={i} style={{ marginBottom: 6 }}>
                  <Text style={[commonStyles.label, { marginBottom: 2 }]}>Gestation {i + 1}:</Text>
                  {g.gestationalSac && g.gestationalSac !== 'Normal' ? <PdfLabelValue label="Gestational sac:" value={g.gestationalSac} isAbnormal /> : null}
                  {g.crl ? <PdfLabelValue label="CRL:" value={`${g.crl} mm`} /> : null}
                  {g.fhr ? <PdfLabelValue label="FHR:" value={formatFHR(g.fhr)} isAbnormal={isFHRNotDetected(g.fhr) || parseFloat(g.fhr) < 100 || parseFloat(g.fhr) > 180} /> : null}
                  {g.yolkSac ? <PdfLabelValue label="Yolk sac:" value={`${g.yolkSac} mm`} /> : null}
                  {g.subchorionic ? <PdfLabelValue label="Subchorionic hemorrhage:" value={g.subchorionic} isAbnormal={g.subchorionic !== 'None'} /> : null}
                  {g.subchronicSize ? <PdfLabelValue label="Subchorionic size:" value={g.subchronicSize} isAbnormal /> : null}
                  {concordance ? <PdfLabelValue label="Concordance:" value={concordance.text} isAbnormal={concordance.isDiscordant} /> : null}
                </View>
              );
            })}

            <PdfMaternalSections maternal={maternal} />
          </View>
        </View>

        <PdfFooter facilityInfo={facilityInfo} />
      </Page>
    </Document>
  );
}
